import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Search, MessageCircle, Phone, ArrowRight, HelpCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FaqPage = () => {
  const [categories, setCategories] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, faqsRes] = await Promise.all([
        axios.get(`${API_URL}/api/faq/categories`),
        axios.get(`${API_URL}/api/faq/public`)
      ]);
      setCategories(categoriesRes.data);
      setFaqs(faqsRes.data);
    } catch (error) {
      console.error('Error fetching FAQ data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const searchFaqs = async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }
      
      setIsSearching(true);
      try {
        const response = await axios.get(`${API_URL}/api/faq/search?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(response.data);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(searchFaqs, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Filter FAQs based on category
  const filteredFaqs = activeCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);

  // Group FAQs by category for display
  const faqsByCategory = filteredFaqs.reduce((acc, faq) => {
    if (!acc[faq.category]) acc[faq.category] = [];
    acc[faq.category].push(faq);
    return acc;
  }, {});

  // Display search results or regular FAQs
  const displayFaqs = searchQuery.trim().length >= 2 ? searchResults : filteredFaqs;
  const isShowingSearchResults = searchQuery.trim().length >= 2;

  return (
    <>
      <SEO 
        title="Frequently Asked Questions - C3 Contracting"
        description="Find answers to common questions about C3 Contracting's services, process, pricing, timelines, and more. Get the information you need about your construction project."
        keywords="FAQ, questions, answers, construction questions, home renovation FAQ, contractor FAQ, Charlotte contractor"
      />
      
      <div className="min-h-screen flex flex-col bg-slate-50">
        <Header />
        
        {/* Hero Section */}
        <section className="relative pt-52 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="absolute inset-0 opacity-10 bg-gradient-to-b from-cyan-500/20 to-transparent"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center max-w-3xl mx-auto">
              <div className="w-16 h-16 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <HelpCircle className="w-8 h-8 text-cyan-400" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                Frequently Asked <span className="text-cyan-400">Questions</span>
              </h1>
              <p className="text-xl text-slate-300 mb-8">
                Find answers to common questions about our services, process, and what to expect when working with C3 Contracting.
              </p>
              
              {/* Search Bar */}
              <div className="relative max-w-xl mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search for answers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 text-base bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:bg-white/15 focus:border-cyan-500 rounded-xl"
                />
                {isSearching && (
                  <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cyan-400 animate-spin" />
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="flex-1 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Sidebar - Category Filter */}
              <aside className="lg:w-64 flex-shrink-0">
                <div className="sticky top-28 bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                  <h3 className="font-semibold text-slate-800 mb-4">Categories</h3>
                  <nav className="space-y-1">
                    <button
                      onClick={() => { setActiveCategory('all'); setSearchQuery(''); }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeCategory === 'all' && !isShowingSearchResults
                          ? 'bg-cyan-50 text-cyan-700 font-medium'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      All Questions
                      <span className="float-right text-slate-400">{faqs.length}</span>
                    </button>
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => { setActiveCategory(category.name); setSearchQuery(''); }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          activeCategory === category.name && !isShowingSearchResults
                            ? 'bg-cyan-50 text-cyan-700 font-medium'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {category.name}
                        <span className="float-right text-slate-400">{category.faq_count}</span>
                      </button>
                    ))}
                  </nav>
                </div>
              </aside>

              {/* FAQ Content */}
              <main className="flex-1 min-w-0">
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 text-cyan-600 animate-spin" />
                  </div>
                ) : isShowingSearchResults ? (
                  // Search Results View
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                      <h2 className="font-semibold text-slate-800">
                        Search Results
                        <span className="ml-2 text-sm font-normal text-slate-500">
                          ({searchResults.length} {searchResults.length === 1 ? 'result' : 'results'} for "{searchQuery}")
                        </span>
                      </h2>
                    </div>
                    {searchResults.length === 0 ? (
                      <div className="p-8 text-center">
                        <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-slate-700 mb-2">No results found</h3>
                        <p className="text-slate-500 mb-4">Try a different search term or browse by category</p>
                        <Button
                          variant="outline"
                          onClick={() => setSearchQuery('')}
                        >
                          Clear Search
                        </Button>
                      </div>
                    ) : (
                      <Accordion type="single" collapsible className="divide-y divide-slate-100">
                        {searchResults.map((faq, index) => (
                          <AccordionItem key={faq.id} value={faq.id} className="border-0">
                            <AccordionTrigger className="px-6 py-4 hover:bg-slate-50 hover:no-underline text-left">
                              <div className="flex-1 pr-4">
                                <span className="text-xs text-cyan-600 font-medium mb-1 block">{faq.category}</span>
                                <span className="text-slate-800 font-medium">{faq.question}</span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-6 pb-4">
                              <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{faq.answer}</p>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    )}
                  </div>
                ) : (
                  // Category View
                  <div className="space-y-6">
                    {activeCategory === 'all' ? (
                      // Show all categories grouped
                      Object.entries(faqsByCategory).map(([categoryName, categoryFaqs]) => (
                        <div key={categoryName} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                            <h2 className="font-semibold text-slate-800">{categoryName}</h2>
                          </div>
                          <Accordion type="single" collapsible className="divide-y divide-slate-100">
                            {categoryFaqs.map((faq) => (
                              <AccordionItem key={faq.id} value={faq.id} className="border-0">
                                <AccordionTrigger className="px-6 py-4 hover:bg-slate-50 hover:no-underline text-left">
                                  <span className="text-slate-800 font-medium pr-4">{faq.question}</span>
                                </AccordionTrigger>
                                <AccordionContent className="px-6 pb-4">
                                  <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{faq.answer}</p>
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </div>
                      ))
                    ) : (
                      // Show single category
                      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                          <h2 className="font-semibold text-slate-800">{activeCategory}</h2>
                          <p className="text-sm text-slate-500 mt-1">
                            {categories.find(c => c.name === activeCategory)?.description}
                          </p>
                        </div>
                        {filteredFaqs.length === 0 ? (
                          <div className="p-8 text-center">
                            <p className="text-slate-500">No FAQs in this category yet.</p>
                          </div>
                        ) : (
                          <Accordion type="single" collapsible className="divide-y divide-slate-100">
                            {filteredFaqs.map((faq) => (
                              <AccordionItem key={faq.id} value={faq.id} className="border-0">
                                <AccordionTrigger className="px-6 py-4 hover:bg-slate-50 hover:no-underline text-left">
                                  <span className="text-slate-800 font-medium pr-4">{faq.question}</span>
                                </AccordionTrigger>
                                <AccordionContent className="px-6 pb-4">
                                  <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{faq.answer}</p>
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </main>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-slate-900 to-slate-800">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Still Have Questions?
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Can't find what you're looking for? Our team is here to help. Reach out directly or schedule a free consultation to discuss your project.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white w-full sm:w-auto">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Contact Us
                </Button>
              </Link>
              <Link to="/consultation">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 w-full sm:w-auto">
                  <Phone className="w-5 h-5 mr-2" />
                  Schedule Consultation
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default FaqPage;
